import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Container, Typography } from '@mui/material';
import { Error as ErrorIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import logger from 'services/logger';
import { withTranslation } from 'react-i18next';

/**
 * ErrorBoundary Component
 *
 * Captura errores de React y muestra una interfaz amigable al usuario.
 * Los errores se registran automáticamente en el logger.
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    const { name } = this.props;

    // Registrar error en el logger
    logger.error('React Error Boundary caught an error', error, {
      componentStack: errorInfo.componentStack,
      errorBoundary: name || 'ErrorBoundary'
    });

    // El logger SILENCIA la consola en builds de producción (dev.otrixapp.com lo es), así
    // que la causa real quedaba oculta. Lo emitimos SIEMPRE a la consola del navegador
    // (DevTools) para poder diagnosticar; es log técnico, no UI.
    // eslint-disable-next-line no-console
    console.error(`[OTRIX ErrorBoundary: ${name || 'ErrorBoundary'}]`, error, errorInfo?.componentStack);

    this.setState({ errorInfo });
  }

  handleReset = () => {
    const { onReset } = this.props;

    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });

    // Si hay un callback de reset personalizado, ejecutarlo
    if (onReset) {
      onReset();
    }
  };

  // eslint-disable-next-line class-methods-use-this
  handleReload = () => {
    window.location.reload();
  };

  render() {
    const { children, fallback, t } = this.props;
    const { error, errorInfo, hasError } = this.state;

    if (hasError) {
      // Si hay un fallback personalizado, usarlo
      if (fallback) {
        return fallback({
          error,
          errorInfo,
          resetError: this.handleReset
        });
      }

      // Interfaz de error por defecto
      return (
        <Container maxWidth="md">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '60vh',
              textAlign: 'center',
              padding: 4
            }}
          >
            <ErrorIcon
              sx={{
                fontSize: 80,
                color: 'error.main',
                mb: 3
              }}
            />

            <Typography variant="h4" gutterBottom>
              {t('general.unexpected', { ns: 'errors' })}
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 600 }}>
              {process.env.NODE_ENV === 'development' && error
                ? error.toString()
                : t('general.serverError', { ns: 'errors' })}
            </Typography>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={this.handleReset}
              >
                {t('reset', { ns: 'buttons' })}
              </Button>

              <Button variant="outlined" color="primary" onClick={this.handleReload}>
                {t('reload', { ns: 'buttons', defaultValue: 'Recargar página' })}
              </Button>
            </Box>

            {process.env.NODE_ENV === 'development' && errorInfo && (
              <Box
                sx={{
                  mt: 4,
                  p: 2,
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                  maxWidth: '100%',
                  overflow: 'auto'
                }}
              >
                <Typography variant="caption" component="pre" sx={{ textAlign: 'left', m: 0 }}>
                  {errorInfo.componentStack}
                </Typography>
              </Box>
            )}
          </Box>
        </Container>
      );
    }

    return children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.func,
  name: PropTypes.string,
  onReset: PropTypes.func,
  t: PropTypes.func.isRequired
};

ErrorBoundary.defaultProps = {
  fallback: null,
  name: 'ErrorBoundary',
  onReset: null
};

export default withTranslation()(ErrorBoundary);
